/**
 * Origins (`/origins`) — every site the workspace tracks.
 *
 * Replacement for legacy `OriginsIsland.tsx`. Features per
 * `05-pages.md` §5b:
 *   - Search (name / URL / host, case-insensitive substring).
 *   - Sort (Newest / Name A–Z / Last-checked).
 *   - Expandable host groups (coss `Collapsible`) — sites grouped
 *     by URL host so an org with `www.acme.com` + `docs.acme.com`
 *     reads as "acme.com" with two children.
 *   - Status badges per site via `<StatusBadge>` driven by the site
 *     overview query (errors vs. success).
 *   - "Add origin" primary action — Dialog + validated URL input,
 *     creates site via `useCreateSite()`.
 *
 * Overview data is fetched per-site in parallel via individual
 * `useSiteOverview()` hooks at the row level — react-query dedupes
 * + caches them independently, so scrolling doesn't refetch.
 *
 * Legacy `?legacy_page=<uuid>` bookmarks land here (see __root.tsx
 * redirect map). We surface a toast telling the user why, but can't
 * resolve siteUuid client-side today (the pageUuid → siteUuid map
 * lives on the server; Gap M8 — Stage 5 handover flags).
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { SiteFavicon } from '@/components/primitives/SiteFavicon';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { FetchError } from '@/lib/api';
import type { Site, SiteOverview } from '@/lib/api-types';
import { toastFromError, toastInfo, toastSuccess } from '@/lib/errors';
import { hostOf, relativeTime } from '@/lib/format';
import {
  useActiveOrg,
  useCreateSite,
  useOrigins,
  useSiteOverview,
  useWorkspace,
} from '@/lib/queries';
import { cn } from '@/lib/utils';
import { CaretDown, CaretRight, Globe, MagnifyingGlass, Plus } from '@phosphor-icons/react';
import { Link, createFileRoute, useSearch } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useEffect, useMemo, useState } from 'react';

type OriginsSearch = { legacy_page?: string };

export const Route = createFileRoute('/_app/origins/')({
  component: OriginsPage,
  validateSearch: (input: Record<string, unknown>): OriginsSearch => {
    const legacy = input.legacy_page;
    return typeof legacy === 'string' && legacy.length > 0 ? { legacy_page: legacy } : {};
  },
});

type SortKey = 'created' | 'name' | 'host';

function OriginsPage(): ReactElement {
  const origins = useOrigins({ limit: 200, offset: 0 });
  const { legacy_page } = useSearch({ from: '/_app/origins/' });

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('created');
  const [createOpen, setCreateOpen] = useState(false);

  // Surface the legacy redirect once per landing. `/app/pages/:uuid`
  // bookmarks land on /origins with ?legacy_page — we can't resolve
  // the siteUuid from a pageUuid without a server endpoint (Gap M8),
  // so we explain and drop the user in the origins list.
  useEffect(() => {
    if (!legacy_page) return;
    toastInfo(
      'Page bookmarks have moved',
      'Page links now live under /origins/<site>/pages/<page>. Use search below to find it.',
    );
  }, [legacy_page]);

  const grouped = useMemo(() => {
    if (!origins.data) return [] as Array<{ host: string; sites: Site[] }>;
    const q = search.trim().toLowerCase();
    const filtered = origins.data.sites.filter((s) => {
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.url.toLowerCase().includes(q) ||
        hostOf(s.url).toLowerCase().includes(q) ||
        s.slug.toLowerCase().includes(q)
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'host') return hostOf(a.url).localeCompare(hostOf(b.url));
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    const map = new Map<string, Site[]>();
    for (const site of sorted) {
      const host = hostOf(site.url);
      const bucket = map.get(host) ?? [];
      bucket.push(site);
      map.set(host, bucket);
    }
    return [...map.entries()].map(([host, sites]) => ({ host, sites }));
  }, [origins.data, search, sort]);

  const count = origins.data?.sites.length ?? 0;

  return (
    <Island
      breadcrumb={[{ label: 'Origins' }]}
      actions={
        <Button onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" />
          Add origin
        </Button>
      }
    >
      <div className="space-y-4">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-[var(--color-mute)]">
              {origins.isLoading
                ? 'Loading origins…'
                : `${count} site${count === 1 ? '' : 's'} across ${grouped.length} host${
                    grouped.length === 1 ? '' : 's'
                  }`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass
                aria-hidden="true"
                size={14}
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--color-mute)]"
              />
              <Input
                value={search}
                onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
                placeholder="Search sites…"
                className="w-56 pl-7"
                aria-label="Search sites"
              />
            </div>
            <Select
              value={sort}
              onValueChange={(v) => {
                if (typeof v === 'string') setSort(v as SortKey);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created">Newest first</SelectItem>
                <SelectItem value="name">Name A–Z</SelectItem>
                <SelectItem value="host">Host</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </header>

        {origins.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {origins.isError && !origins.isLoading && (
          <EmptyState
            icon={Globe}
            title="We couldn't load origins"
            description={
              origins.error instanceof FetchError
                ? origins.error.message
                : 'Check your connection and try again.'
            }
            action={
              <Button variant="outline" onClick={() => origins.refetch()}>
                Retry
              </Button>
            }
          />
        )}

        {!origins.isLoading &&
          !origins.isError &&
          grouped.length === 0 &&
          (search ? (
            <EmptyState
              icon={MagnifyingGlass}
              title="No sites match your search"
              description={`Nothing with "${search}" across name, URL, or slug.`}
              action={
                <Button variant="ghost" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Globe}
              title="No origins tracked yet"
              description="Add the first site you want to monitor — page-level audits come next."
              action={
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus aria-hidden="true" />
                  Add origin
                </Button>
              }
            />
          ))}

        {grouped.map((group) => (
          <HostGroup key={group.host} host={group.host} sites={group.sites} />
        ))}
      </div>

      <CreateSiteDialog open={createOpen} onOpenChange={setCreateOpen} />
    </Island>
  );
}

// -----------------------------------------------------------------
// Host group (expandable)
// -----------------------------------------------------------------

function HostGroup({ host, sites }: { host: string; sites: Site[] }): ReactElement {
  return (
    <Collapsible defaultOpen>
      <section className="overflow-hidden rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
        <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-left outline-none transition-colors hover:bg-[var(--color-paper-alt)]/40 data-[state=open]:border-b data-[state=open]:border-[var(--color-line-soft)]">
          <div className="flex items-center gap-2">
            <CaretRight
              aria-hidden="true"
              size={14}
              className="text-[var(--color-mute)] transition-transform group-data-[state=open]:hidden"
            />
            <CaretDown
              aria-hidden="true"
              size={14}
              className="hidden text-[var(--color-mute)] group-data-[state=open]:block"
            />
            <span className="font-medium text-[var(--color-ink)]">{host}</span>
            <span className="text-xs text-[var(--color-mute)]">
              {sites.length} site{sites.length === 1 ? '' : 's'}
            </span>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="divide-y divide-[var(--color-line-soft)]">
            {sites.map((s) => (
              <SiteRow key={s.uuid} site={s} />
            ))}
          </ul>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

function SiteRow({ site }: { site: Site }): ReactElement {
  const overview = useSiteOverview(site.uuid);
  return (
    <li>
      <Link
        to="/origins/$siteUuid"
        params={{ siteUuid: site.uuid }}
        className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-[var(--color-paper-alt)]/40"
      >
        <SiteFavicon url={site.url} size={30} className="rounded-sm" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-[var(--color-ink)]">{site.name}</div>
          <div className="truncate text-xs text-[var(--color-mute)]">{site.url}</div>
        </div>
        <LastRun overview={overview.data} loading={overview.isLoading} />
        <MonitoringPill enabled={site.monitoring_enabled} />
      </Link>
    </li>
  );
}

function LastRun({
  overview,
  loading,
}: {
  overview: SiteOverview | undefined;
  loading: boolean;
}): ReactElement {
  if (loading) return <Skeleton className="h-5 w-20" />;
  if (!overview || !overview.last_run_at) {
    return <span className="w-24 text-right text-xs text-[var(--color-mute)]">never</span>;
  }
  const status: 'success' | 'error' =
    overview.error_runs > 0 && overview.total_runs > 0 ? 'error' : 'success';
  return (
    <div className="flex items-center gap-2">
      <StatusBadge status={status} />
      <span className="w-24 text-right text-xs text-[var(--color-mute)]">
        {relativeTime(overview.last_run_at)}
      </span>
    </div>
  );
}

function MonitoringPill({ enabled }: { enabled: boolean }): ReactElement {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        enabled
          ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
          : 'bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)]',
      )}
    >
      {enabled ? 'monitoring' : 'off'}
    </span>
  );
}

// -----------------------------------------------------------------
// Create-site dialog
// -----------------------------------------------------------------

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function CreateSiteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}): ReactElement {
  const workspace = useWorkspace();
  const activeOrg = useActiveOrg();
  const mutation = useCreateSite();

  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [teamUuid, setTeamUuid] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | undefined>(undefined);

  // Build the team list the user can choose from. We surface every
  // team across every org the user belongs to (annotated with the
  // org name), not just the active org's teams. Earlier passes
  // scoped this to `auth_provider_id === activeOrg.orgId` and
  // disabled the submit when that lookup returned `null` — which
  // can happen in legitimate states (Clerk org created, Supabase
  // org row not yet synced; org-id casing mismatch; workspace API
  // not yet loaded with the latest org). Showing every team and
  // letting the user pick avoids the dead-end greyed-button UX
  // without weakening the API-side tenancy check (the server
  // re-verifies team ownership on `POST /v1/management/teams/.../sites`).
  type TeamOption = { teamUuid: string; teamName: string; orgName: string; orgUuid: string };
  const teamOptions: TeamOption[] = useMemo(() => {
    const orgs = workspace.data?.organisations ?? [];
    const out: TeamOption[] = [];
    for (const org of orgs) {
      for (const team of org.teams) {
        out.push({
          teamUuid: team.uuid,
          teamName: team.name,
          orgName: org.name,
          orgUuid: org.uuid,
        });
      }
    }
    return out;
  }, [workspace.data]);

  // Default selection: prefer a team in the **active** org (so the
  // org-switcher state matches the destination); fall back to the
  // first team overall when active-org resolution doesn't match
  // anything (race during onboarding or stale workspace data).
  useEffect(() => {
    if (teamUuid) return;
    const fallback = teamOptions[0];
    if (!fallback) return;
    const activeOrgRow = workspace.data?.organisations.find(
      (o) => o.auth_provider_id === activeOrg.orgId,
    );
    const preferredTeam = activeOrgRow?.teams[0];
    setTeamUuid(preferredTeam?.uuid ?? fallback.teamUuid);
  }, [teamOptions, teamUuid, workspace.data, activeOrg.orgId]);

  // Reset on close. Includes `teamUuid` so reopening on a different
  // active org picks up the new default rather than the previous
  // org's stale selection.
  useEffect(() => {
    if (open) return;
    setUrl('');
    setName('');
    setTeamUuid(undefined);
    setFormError(undefined);
  }, [open]);

  const workspaceLoading = !workspace.data || !activeOrg.isLoaded;
  const hasNoTeams = !workspaceLoading && teamOptions.length === 0;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(undefined);

    if (!teamUuid) {
      setFormError(
        teamOptions.length === 0
          ? 'No team available in this workspace. Reload the page or contact support.'
          : 'Pick a team for this origin.',
      );
      return;
    }

    // Accept either a full URL ("https://example.com") or a bare
    // host ("example.com", "www.example.com"). Most users type the
    // host without the scheme; the API stores a normalised URL so
    // we canonicalise here before validating.
    const trimmed = url.trim();
    if (trimmed.length === 0) {
      setFormError('Enter a website URL.');
      return;
    }
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    let parsed: URL;
    try {
      parsed = new URL(candidate);
    } catch {
      setFormError('Enter a valid website URL, e.g. example.com or https://example.com.');
      return;
    }
    // `new URL("https://")` succeeds with an empty host; reject
    // explicitly so we don't post garbage. Also require at least one
    // dot in the hostname — a bare "localhost" isn't a real origin.
    if (!parsed.hostname || !parsed.hostname.includes('.')) {
      setFormError('Enter a valid website URL with a hostname, e.g. example.com.');
      return;
    }

    const derivedName = name.trim() || parsed.hostname;
    const slug = slugify(derivedName);

    try {
      await mutation.mutateAsync({
        teamUuid,
        body: {
          slug,
          name: derivedName,
          url: parsed.toString(),
          is_public: false,
          monitoring_enabled: false,
        },
      });
      toastSuccess('Origin added', `${derivedName} is now tracked.`);
      onOpenChange(false);
    } catch (err) {
      // QueryCache toast already fired; surface inline too for field context.
      if (err instanceof FetchError) {
        setFormError(err.message);
      } else if (err instanceof Error) {
        setFormError(err.message);
      } else {
        toastFromError(err);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add origin</DialogTitle>
          <DialogDescription>
            Track audits for a new site. We infer the host from the URL — give it a friendly name to
            help teammates find it later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <DialogPanel>
            <div className="space-y-4">
              <Field>
                <FieldLabel htmlFor="origin-url">Website URL</FieldLabel>
                <Input
                  id="origin-url"
                  type="text"
                  inputMode="url"
                  placeholder="example.com or https://www.example.com"
                  autoFocus
                  value={url}
                  onChange={(e) => setUrl((e.target as HTMLInputElement).value)}
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="origin-name">Display name (optional)</FieldLabel>
                <Input
                  id="origin-name"
                  placeholder="Acme marketing site"
                  value={name}
                  onChange={(e) => setName((e.target as HTMLInputElement).value)}
                />
              </Field>
              {teamOptions.length >= 1 && (
                <Field>
                  <FieldLabel htmlFor="origin-team">Team</FieldLabel>
                  <Select
                    value={teamUuid}
                    onValueChange={(v) => {
                      if (typeof v === 'string') setTeamUuid(v);
                    }}
                  >
                    <SelectTrigger id="origin-team">
                      <SelectValue placeholder="Select a team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamOptions.map((t) => (
                        <SelectItem key={t.teamUuid} value={t.teamUuid}>
                          {/* When the user belongs to a single org, drop
                              the prefix; otherwise show "<Org> · <Team>"
                              so it's clear which org the origin lands in. */}
                          {workspace.data && workspace.data.organisations.length > 1
                            ? `${t.orgName} · ${t.teamName}`
                            : t.teamName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {hasNoTeams && (
                <Field>
                  <FieldError match>
                    No team is available in this workspace. Try switching organisations or reload
                    the page.
                  </FieldError>
                </Field>
              )}
              {/*
                Form-level error surface. Wrapped in a `<Field>` because
                Base UI's `<Field.Error>` reads from `FieldRootContext`
                and throws Base UI #28 when rendered standalone — which
                would crash the dialog the moment any submit error
                appears.
              */}
              {formError && (
                <Field>
                  <FieldError match>{formError}</FieldError>
                </Field>
              )}
            </div>
          </DialogPanel>
          <DialogFooter>
            <DialogClose render={<Button variant="outline">Cancel</Button>} />
            <Button
              type="submit"
              loading={mutation.isPending || workspaceLoading}
              disabled={hasNoTeams}
            >
              Add origin
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
